/* eslint-disable no-nested-ternary */
import React, { useMemo, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
// import GridLayout from "react-grid-layout";
import { Responsive, WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { Box, Card, Button } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import {
  GET_WIDGET_DASHBOARD_LAYOUT,
  UPDATE_WIDGET_DASHBOARD_LAYOUT,
  GET_WIDGET_DEFINITIONS,
  DELETE_WIDGET_DASHBOARD_FOR_USER,
  DELETE_WIDGET_DASHBOARD_FOR_COMPANY,
} from "lib/constants";
import useMediaQuery from "@mui/material/useMediaQuery";
import { getAPICall, postAPICall, deleteAPICall } from "lib/callApi";
import WidgetDashboardModal from "./WidgetDashboardModal";
import "./styles/widget.scss";
import RevenueGauge from "./components/RevenueGauge";
import WidgetBox from "./components/WidgetBox";
import KpiBreakdownWidget from "./components/KpiBreakdownWidget";
import MonthlySnapshot from "./components/PerryGroup/MonthlySnapshot";
import ListingSnapshot from "./components/PerryGroup/ListingSnapshot";
import Leaderboard from "./Leaderboard/Leaderboard";
import KpiView from "./KpiView/KpiView";
import PointGoal from "./PointGoal/PointGoal";
import SingleNumberDisplay from "./components/SingleNumberDisplay";

const componentMap = {
  RevenueGauge,
  WidgetBox,
  KpiBreakdownWidget,
  MonthlySnapshot,
  ListingSnapshot,
  Leaderboard,
  KpiView,
  PointGoal,
  SingleNumberDisplay,
};

export default function WidgetGrid({ selectedTab, selectedUser, selectedTeam, selectedTeams }) {
  const user = useSelector((state) => state.loginUser.loginUserState);
  const ResponsiveReactGridLayout = useMemo(() => WidthProvider(Responsive), []);
  const [widgets, setWidgets] = useState({ widgetList: [] });
  const [widgetDefinitions, setWidgetDefinitions] = useState([]);
  const [open, setOpen] = useState(false);
  const isMobileDisplay = useMediaQuery((theme) => theme.breakpoints.only("xs"));

  const metadataToOmit = [
    "componentName",
    "singleInstanceOnly",
    "dataSource",
    "integrationId",
    "displayName",
    "widgetTitle",
    "isCustomizable",
    "dataGrid",
  ];

  const handleCloseWidgetModal = () => setOpen(false);

  const handleOpenWidgetModal = async () => {
    const result = await getAPICall(GET_WIDGET_DEFINITIONS, true);
    if (result?.data?.success) {
      setWidgetDefinitions(result.data.widgets);
      setOpen(true);
    }
  };

  const getWidgetDashboardForUser = async () => {
    const result = await getAPICall(GET_WIDGET_DASHBOARD_LAYOUT, true);
    if (result?.data?.success) {
      setWidgets({ widgetList: result.data.dashboard });
    }
  };

  const serializeLayout = (layout) =>
    layout.map((widget) => {
      const obj = {
        dataGrid: {
          x: widget.dataGrid.x,
          y: widget.dataGrid.y,
          w: widget.dataGrid.w,
          h: widget.dataGrid.h,
        },
      };

      // Add any misc meta we need
      Object.entries(widget).forEach(([key, value]) => {
        if (!metadataToOmit.includes(key)) {
          obj[key] = value;
        }
      });
      return obj;
    });

  const saveWidgetDashboard = async (layout, userDashboard = true) => {
    if (!layout) {
      return;
    }
    const serialized = serializeLayout(layout);
    const response = await postAPICall(UPDATE_WIDGET_DASHBOARD_LAYOUT, true, {
      userId: userDashboard ? user.id : null,
      companyId: user.companyId,
      dashboard: JSON.stringify(serialized),
    });
    if (!response?.data?.success) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: "Something Went wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const updateWidgetMetadata = (gridKey, values) => {
    const widgetIndex = widgets.widgetList.findIndex((w) => w.gridKey === gridKey);
    if (widgetIndex !== -1) {
      Object.entries(values).forEach(([key, value]) => {
        widgets.widgetList[widgetIndex][key] = value;
      });
      saveWidgetDashboard(widgets.widgetList);
    }
  };

  const deleteUserWidgetDashboard = async () => {
    const response = await deleteAPICall(DELETE_WIDGET_DASHBOARD_FOR_USER, true, null);

    if (response?.data?.success) {
      setWidgets({ widgetList: response.data.dashboard });
    } else {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: "Something Went wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const deleteCompanyWidgetDashboard = async () => {
    const response = await deleteAPICall(DELETE_WIDGET_DASHBOARD_FOR_COMPANY, true, null);

    if (response?.data?.success) {
      setWidgets({ widgetList: response.data.dashboard });
    } else {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: "Something Went wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const didItChange = (oldItem, newItem) => {
    if (!oldItem || !newItem) return true;
    return !(
      oldItem.x === newItem.x &&
      oldItem.y === newItem.y &&
      oldItem.w === newItem.w &&
      oldItem.h === newItem.h
    );
  };

  const widgetPositionCompare = (a, b) => {
    if (a.y < b.y) {
      return -1;
    }
    if (a.y > b.y) {
      return 1;
    }
    return 0;
  };

  const addMetadataToGridLayout = (layout) =>
    layout.map((widget) => {
      const widgetFromLayout = widgets.widgetList.find((w) => w.gridKey === widget.i);
      return {
        ...widgetFromLayout,
        dataGrid: {
          x: widget.x,
          y: widget.y,
          w: widget.w,
          h: widget.h,
          ...(widget.minW && { minW: widget.minW }),
          ...(widget.minH && { minH: widget.minH }),
          ...(widget.maxW && { maxW: widget.maxW }),
          ...(widget.maxH && { maxH: widget.maxH }),
          isDraggable: widget.isDraggable,
          isResizable: widget.isResizable,
          isBounded: true,
        },
      };
    });

  const onLayoutChange = (layout, oldItem, newItem) => {
    if (didItChange(oldItem, newItem)) {
      // Sort/serialize layout
      layout.sort(widgetPositionCompare);
      const widgetLayout = addMetadataToGridLayout(layout);

      // Update widgets state without re-render (for modal)
      widgets.widgetList = widgetLayout;

      // Save dashboard for user
      saveWidgetDashboard(widgetLayout);
    }
  };

  const handleModalSave = (widgetLayout) => {
    setWidgets({ widgetList: widgetLayout });
    saveWidgetDashboard(widgetLayout);
  };

  const handleSaveCompanyDefault = (widgetLayout) => {
    setWidgets({ widgetList: widgetLayout });
    saveWidgetDashboard(widgetLayout, false);
  };

  useEffect(() => {
    getWidgetDashboardForUser();
  }, []);

  const renderComponent = (widget) => {
    const { componentName } = widget;
    const Component = componentMap[componentName];
    return (
      <Component
        selectedUser={selectedUser}
        selectedTab={selectedTab}
        widget={widget}
        updateWidgetMetadata={updateWidgetMetadata}
        selectedTeam={selectedTeam}
        selectedTeams={selectedTeams}
      />
    );
  };

  return (
    <>
      <Box margin={{ xs: "0 -16px", lg: "0 -16px 0 0" }}>
        <ResponsiveReactGridLayout
          margin={[16, 16]}
          rowHeight={152.5}
          cols={{ lg: 2, md: 2, sm: 2, xs: 2, xxs: 2 }}
          isDroppable
          // isResizable={false}
          draggableHandle=".dragHandle"
          onDragStop={onLayoutChange}
          onResizeStop={onLayoutChange}
        >
          {widgets.widgetList?.map((widget) => (
            <Card key={widget.gridKey} data-grid={{ ...widget.dataGrid }} p={2}>
              {renderComponent(widget)}
            </Card>
          ))}
        </ResponsiveReactGridLayout>
        {!isMobileDisplay && (
          <Box
            sx={{
              float: "right",
            }}
          >
            <Button type="submit" color="primaryRed" onClick={handleOpenWidgetModal}>
              Customize Widget Layout
            </Button>
          </Box>
        )}
      </Box>
      <WidgetDashboardModal
        open={open}
        handleClose={handleCloseWidgetModal}
        user={user}
        widgets={widgets.widgetList}
        widgetDefinitions={widgetDefinitions}
        handleSave={handleModalSave}
        handleDeleteUserDashboard={deleteUserWidgetDashboard}
        handleSaveCompanyDefault={handleSaveCompanyDefault}
        handleDeleteCompanyDashboard={deleteCompanyWidgetDashboard}
      />
    </>
  );
}

WidgetGrid.propTypes = {
  selectedTab: PropTypes.number.isRequired,
  selectedUser: PropTypes.number.isRequired,
};
